import type { Invoice, InvoiceLineItem } from 'payments-api/model';
import type { User } from 'payments-api/model/user';
import clsx from 'clsx';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { useMemo, useState } from 'react';
import { makePath } from 'utils/routes';

import { Icon } from '@fragment/ui/src/components/Icon';
import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table/helpers';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { getRoleColor } from './constants';

const PRODUCT_COLORS = [
  'bg-amber',
  'bg-royal',
  'bg-forest',
  'bg-purple',
  'bg-magenta',
  'bg-asset-green',
  'bg-liability-red',
  'bg-income-blue',
  'bg-expense-yellow',
] as const;

const getProductColor = (productId: string): string => {
  const hash = productId
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return PRODUCT_COLORS[hash % PRODUCT_COLORS.length];
};

const InvoiceIcon = () => (
  <span className="text-main-500">
    <Icon type="invoice" />
  </span>
);

export const InvoiceColumnHeaders = () => (
  <div className="grid grid-flow-col auto-cols-fr gap-x-[1ch] px-f1 py-f1 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-3">Invoice</div>
    <div className="col-span-2">Created</div>
    <div className="col-span-1 text-right">Amount</div>
  </div>
);

const ItemColumnHeaders = () => (
  <div className="grid grid-flow-col auto-cols-fr gap-x-[1ch] px-f1 py-f1 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-3">Name</div>
    <div className="col-span-1 text-right">Amount</div>
  </div>
);

// Helper to check if payout party matches a given externalId
const isPayoutPartyMatch = (
  item: InvoiceLineItem,
  externalId: string
): boolean => item.user_id === externalId;

type InvoiceRow = {
  id: string;
  created: string;
  totalForUser: bigint;
};

type ItemRow = {
  id: string;
  description: string;
  productId: string;
  amount: string;
};

type UserViewProps = {
  user: User;
  userInvoices: Invoice[];
  externalId: string;
  workspaceId: string;
};

// Format amount as currency
const formatCurrency = (amount: string | bigint): JSX.Element => {
  const amountNum =
    typeof amount === 'string' ? parseInt(amount, 10) : Number(amount);
  const formatted = (amountNum / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (
    <span className="inline-flex items-baseline gap-x-[1ch]">
      <span>$</span>
      <span>{formatted}</span>
    </span>
  );
};

// Define schema for items table
const itemsSchema: readonly ColumnDefinition<ItemRow>[] = [
  {
    title: 'Name',
    colspan: 'col-span-3',
    format: (row: ItemRow) => (
      <div className="flex items-center gap-x-[0.25lh]">
        <div
          className={clsx(
            'w-[14px] h-[14px] rounded-[2px] shrink-0',
            getProductColor(row.productId)
          )}
        />
        <span>{row.description}</span>
      </div>
    ),
  },
  {
    title: 'Amount',
    justify: 'right' as const,
    colspan: 'col-span-1',
    format: (row: ItemRow) => (
      <span className="ml-auto">{formatCurrency(row.amount)}</span>
    ),
  },
];

/**
 * UserView component - Shared logic and UI for displaying user information
 * Used by UserDetailPage
 */
export const UserView = ({
  user,
  userInvoices,
  externalId,
  workspaceId,
}: UserViewProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<'invoices' | 'items'>('invoices');

  const tabOptions = useMemo(
    () => [
      { label: 'Invoices', value: 'invoices' as const },
      { label: 'Items', value: 'items' as const },
    ],
    []
  );

  // Prepare invoice data for SubTable
  const invoiceRows = useMemo(
    (): InvoiceRow[] =>
      userInvoices.map((invoice) => {
        // Check if this user is involved as a seller
        const userItemsInInvoice =
          invoice.lineItems?.filter((item) =>
            isPayoutPartyMatch(item, externalId)
          ) || [];

        const totalForUser = userItemsInInvoice.reduce(
          (sum, item) => sum + BigInt(item.amount),
          BigInt(0)
        );

        return {
          id: invoice.id,
          created: invoice.created,
          totalForUser,
        };
      }),
    [userInvoices, externalId]
  );

  // Prepare item data for SubTable
  const itemRows = useMemo(
    (): ItemRow[] =>
      userInvoices.flatMap((invoice) =>
        (invoice.lineItems || [])
          .filter((item) => isPayoutPartyMatch(item, externalId))
          .map((item) => ({
            id: item.id,
            description: item.description,
            productId: item.product_id,
            amount: item.amount,
          }))
      ),
    [userInvoices, externalId]
  );

  // Define schema for invoices table
  const invoicesSchema: readonly ColumnDefinition<InvoiceRow>[] = useMemo(
    () => [
      {
        title: 'Invoice',
        colspan: 'col-span-3',
        // eslint-disable-next-line react/no-unstable-nested-components
        format: (row: InvoiceRow) => (
          <div className="flex items-center gap-x-1">
            <InvoiceIcon />
            <span>{row.id}</span>
          </div>
        ),
      },
      {
        title: 'Created',
        colspan: 'col-span-2',
        format: (row: InvoiceRow) =>
          new Date(row.created).toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          }),
      },
      {
        title: 'Amount',
        justify: 'right' as const,
        colspan: 'col-span-1',
        // eslint-disable-next-line react/no-unstable-nested-components
        format: (row: InvoiceRow) => (
          <span className="ml-auto">{formatCurrency(row.totalForUser)}</span>
        ),
      },
    ],
    []
  );

  const getInvoicePath = (invoice: InvoiceRow): string =>
    makePath(['w', workspaceId, 'payments', 'invoices', invoice.id], {
      absolute: true,
    });

  const getItemPath = (_item: ItemRow): string =>
    // Future: navigate to item detail or show item details
    // For now, return current path to make rows non-navigable
    '#';
  const { totalPayouts, totalPayins } = useMemo(() => {
    let payouts = BigInt(0);
    let payins = BigInt(0);
    userInvoices.forEach((invoice) => {
      (invoice.lineItems ?? [])
        .filter((item) => item.user_id === externalId)
        .forEach((item) => {
          if (item.type === InvoiceLineItemType.payout) {
            payouts += BigInt(item.amount);
          } else {
            payins += BigInt(item.amount);
          }
        });
    });
    return { totalPayouts: payouts, totalPayins: payins };
  }, [userInvoices, externalId]);

  return (
    <>
      {/* ID row: badge (circle + name) aligned to top of row */}
      <div className="flex items-start px-f1 py-f1 border-b-[0.5px] border-main-200 w-full h-f8">
        <div className="flex items-center gap-x-[0.25lh]">
          <div
            className={clsx(
              'w-[20px] h-[20px] rounded-full shrink-0',
              getRoleColor(user.role)
            )}
          />
          <span className="text-lg text-main">{user.externalId}</span>
        </div>
      </div>

      {/* Totals rows */}
      <div className="flex items-center border-b-[0.5px] border-main-200 w-full">
        <div
          className={clsx(
            'px-f1 py-f1 text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0',
            kLabelColumnWidth
          )}
        >
          Total Payins
        </div>
        <div className="px-f1 py-f1 text-xmd text-main flex-1">
          {formatCurrency(totalPayins)}
        </div>
      </div>
      <div className="flex items-start h-f8 border-b-[0.5px] border-main-200 w-full">
        <div
          className={clsx(
            'px-f1 pt-f1 text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full',
            kLabelColumnWidth
          )}
        >
          Total Payouts
        </div>
        <div className="px-f1 pt-f1 text-xmd text-main flex-1">
          {formatCurrency(totalPayouts)}
        </div>
      </div>

      {/* Tabs and Content */}
      <div className="flex flex-col w-full min-h-f1">
        {/* Tab row with filler */}
        <div className="flex items-end w-full">
          <TabsV2
            options={tabOptions}
            selectedValue={activeTab}
            onClick={setActiveTab}
            data-testid="user-view-tabs"
          />
          <div className="flex-1 border-b-[0.5px] border-main-200" />
        </div>

        {/* Column headers for invoices tab */}
        {activeTab === 'invoices' && <InvoiceColumnHeaders />}

        {/* Tab Content */}
        {activeTab === 'invoices' && (
          <NavTableV2
            data={invoiceRows}
            schema={invoicesSchema}
            getPath={getInvoicePath}
            textSize="xmd"
            showHeader={false}
            stickyHeader={false}
            emptyValue={{
              Invoice: (
                <span className="text-main-500">
                  No invoices found for this user
                </span>
              ),
              Created: '',
              Amount: '',
            }}
          />
        )}

        {activeTab === 'items' && <ItemColumnHeaders />}

        {activeTab === 'items' && (
          <NavTableV2
            data={itemRows}
            schema={itemsSchema}
            getPath={getItemPath}
            textSize="xmd"
            showHeader={false}
            stickyHeader={false}
            emptyValue={{
              Name: (
                <span className="text-main-500">
                  No items found for this user
                </span>
              ),
              Amount: '',
            }}
          />
        )}
      </div>
    </>
  );
};
